import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminLoginPage = () => {
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [adminCode, setAdminCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  // If already logged in as admin, redirect
  if (user?.is_admin) {
    navigate('/admin');
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isRegister) {
        const response = await axios.post(`${API}/api/auth/admin/register`, {
          name,
          email,
          password,
          admin_code: adminCode
        });
        login(response.data.user, response.data.token);
        toast.success('Admin account created!');
      } else {
        const response = await axios.post(`${API}/api/auth/login`, {
          email,
          password
        });
        
        if (!response.data.user.is_admin) {
          toast.error('This account does not have admin privileges');
          setLoading(false);
          return;
        }
        
        login(response.data.user, response.data.token);
        toast.success('Welcome back, Admin!');
      }
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-4" data-testid="admin-login-page">
      <div className="w-full max-w-md bg-white p-8 shadow-lg">
        {/* Logo */}
        <Link to="/" className="block text-center mb-8">
          <h1 className="font-heading text-2xl font-semibold">BLUSH & GO</h1>
          <p className="text-xs uppercase tracking-widest text-neutral-500 mt-1">Admin Portal</p>
        </Link>

        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-neutral-100 flex items-center justify-center">
            <ShieldCheck className="w-8 h-8" />
          </div>
        </div>

        <h2 className="font-heading text-2xl font-medium text-center mb-8">
          {isRegister ? 'Create Admin Account' : 'Admin Sign In'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-5">
          {isRegister && (
            <div>
              <Label htmlFor="name" className="text-xs uppercase tracking-widest">
                Name
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-2 h-12 rounded-none border-neutral-300"
                placeholder="Admin name"
                required={isRegister}
                data-testid="admin-name-input"
              />
            </div>
          )}

          <div>
            <Label htmlFor="email" className="text-xs uppercase tracking-widest">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 h-12 rounded-none border-neutral-300"
              placeholder="admin@blushandgo.ca"
              required
              data-testid="admin-email-input"
            />
          </div>

          <div>
            <Label htmlFor="password" className="text-xs uppercase tracking-widest">
              Password
            </Label>
            <div className="relative mt-2">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 rounded-none border-neutral-300 pr-10"
                placeholder="Enter password"
                required
                data-testid="admin-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {isRegister && (
            <div>
              <Label htmlFor="adminCode" className="text-xs uppercase tracking-widest">
                Admin Code
              </Label>
              <Input
                id="adminCode"
                type="password"
                value={adminCode}
                onChange={(e) => setAdminCode(e.target.value)}
                className="mt-2 h-12 rounded-none border-neutral-300"
                placeholder="Enter admin code"
                required={isRegister}
                data-testid="admin-code-input"
              />
              <p className="text-xs text-neutral-500 mt-1">Contact owner for admin code</p>
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-[#1A1A1A] text-white hover:bg-neutral-800 rounded-none uppercase tracking-widest text-xs font-bold"
            data-testid="admin-submit-btn"
          >
            {loading ? <span className="spinner"></span> : (isRegister ? 'Create Admin Account' : 'Sign In')}
          </Button>
        </form>

        <p className="text-center text-sm text-neutral-500 mt-6">
          {isRegister ? 'Already have an admin account?' : "Don't have an admin account?"}{' '}
          <button
            type="button"
            onClick={() => setIsRegister(!isRegister)}
            className="text-neutral-900 hover:underline"
            data-testid="toggle-admin-mode"
          >
            {isRegister ? 'Sign in' : 'Register'}
          </button>
        </p>

        <div className="mt-6 pt-6 border-t text-center">
          <Link to="/" className="text-xs uppercase tracking-widest text-neutral-500 hover:text-neutral-900">
            Back to Store
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
